import { useEffect, useRef, useState } from "react";
import { Formik } from "formik";
import { Slide, ToastContainer, toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { DefaultLayout } from "../../layouts/Default";
import { Main } from "../../themes/Default/components/Main";
import { Tabs } from "../../themes/Default/components/Tabs";
import { TopNavigation } from "../../themes/Default/components/TopNavigation";
import { Breadcrumps } from "../../themes/Default/components/Breadcrumps";
import { Link } from "../../themes/Default/components/Breadcrumps/components/Link";
import { Button, ButtonBack } from "../../themes/Default/components/TopNavigation/components/Button";
import { Form } from "../../themes/Default/components/Form";
import { Row } from "../../themes/Default/components/Form/components/Row";
import { Field } from "../../themes/Default/components/Form/components/Row/components/Field";
import { Input } from "../../themes/Default/components/Form/components/Input";
import { Avatar } from "../../themes/Default/components/Form/components/Avatar";
import { Select } from "../../themes/Default/components/Form/components/Select";
import { studentSchoolsManagement } from "../../hooks/api/StudentSchoolsManagement";
import { studentsManagement } from "../../hooks/api/StudentsManagement";
import { prepareSelect } from "../../services/core/Select";
import { useTranslation } from "react-i18next";
import { municipalitiesManagement } from "../../hooks/api/MunicipalitiesManagement/MunicipalitiesManagement";
import Divider from "../../themes/Default/components/Divider/Divider";
import { guardianTypesManagement } from "../../hooks/api/GuardianTypesManagement/GuardianTypesManagement";
import DatePicker from "../../themes/Default/components/Form/components/DatePicker/DatePicker";
import SummerNote from "../../packages/SummerNote/SummerNote";
import { studentAddressManagement } from "../../hooks/api/StudentAddressManagement/StudentAddressManagement";
import { studentGuardianManagement } from "../../hooks/api/StudentGuardianManagement";

const AddStudent = () => {
    const { t } = useTranslation("common");
    const formRef = useRef(null);
    const navigate = useNavigate();
    const { studentId } = useParams();
    const [avatarURL, setAvatarURL] = useState(null);
    const [schools, setSchools] = useState([]);
    const [municipalities, setMunicipalities] = useState([]);
    const [guardianTypes, setGuardianTypes] = useState([]);
    const [formErrors, setFormErrors] = useState([]);
    const { useStudentSchoolsListMutation } = studentSchoolsManagement;
    const { useMunicipalitiesListMutation } = municipalitiesManagement;
    const { useGuardianTypesListMutation } = guardianTypesManagement;
    const { useUpdateStudentsMutation, useViewStudentsMutation, useAddAvatarMutation, useViewAvatarMutation, useDeleteAvatarMutation } = studentsManagement;
    const [studentSchoolsList] = useStudentSchoolsListMutation();
    const [municipalitiesList] = useMunicipalitiesListMutation();
    const [guardianTypesList] = useGuardianTypesListMutation();
    const [updateStudents, { isLoading: isUpdateingStudent }] = useUpdateStudentsMutation();
    const [viewStudents] = useViewStudentsMutation();
    const [addAvatar] = useAddAvatarMutation();
    const [viewAvatar] = useViewAvatarMutation();
    const [deleteAvatar] = useDeleteAvatarMutation();
    const [saving, setSaving] = useState(false);

    const [address, setAddress] = useState([]);
    const { useStudentAddressListMutation } = studentAddressManagement;
    const [studentAddressList] = useStudentAddressListMutation();

    const [guardians, setGuardians] = useState();
    const { useGuardiansListMutation } = studentGuardianManagement;
    const [guardiansList] = useGuardiansListMutation();
    const [disableGuardian2, setDisableGuardian2] = useState(false);

    const gotoGroupsTab = (studentId) => {
        navigate("/students/" + studentId + "/groups");
    };

    const gotoNotesTab = (studentId) => {
        navigate("/students/" + studentId + "/notes");
    };

    const gotoTestsTab = (studentId) => {
        navigate("/students/" + studentId + "/tests");
    };

    const gotoDocumentsTab = (studentId) => {
        navigate("/students/" + studentId + "/documents");
    };

    const gotoCertificatesTab = (studentId) => {
        navigate("/students/" + studentId + "/certificates");
    };

    const gotoContractsTab = (studentId) => {
        navigate("/students/" + studentId + "/contracts");
    };

    const gotoCallsTab = (studentId) => {
        navigate("/students/" + studentId + "/calls");
    };

    const tabs = [
        { title: t("details"), isActive: true },
        // { title: t("students.tests"), onClick: () => gotoTestsTab(studentId) },
        { title: t("students.contract"), onClick: () => gotoContractsTab(studentId) },
        { title: t("calls.title"), onClick: () => gotoCallsTab(studentId) },
        { title: t("groups.title"), onClick: () => gotoGroupsTab(studentId) },
        { title: t("students.certificates"), onClick: () => gotoCertificatesTab(studentId) },
        { title: t("documents"), onClick: () => gotoDocumentsTab(studentId) },
        { title: t("notes"), onClick: () => gotoNotesTab(studentId) },
    ];

    const onSave = () => {
        if (formRef.current) {
            setSaving(true);
            formRef.current.handleSubmit();
        }
    };

    const onSaved = (values) => {
        const body = { ...values };
        updateStudents({ body, studentId }).then((result) => {
            if (result?.error) {
                setFormErrors(result?.error?.data?.error?.violations);
                toast.error(result.error.data.error.responseMessage);
            } else {
                toast.info(result.data.info.responseMessage);
                loadAll();
            }
            setSaving(false);
        });
    };

    const onAvatarRemove = (event) => {
        event.preventDefault();
        deleteAvatar(studentId).then((result) => {
            setAvatarURL(null);
        });
    };

    const loadAll = () => {
        studentSchoolsList().then((result) => {
            let options = [];
            result?.data?.records.map((school) => {
                options.push({
                    id: school.id,
                    name: school.name,
                });
            });
            setSchools(prepareSelect(options));
        });
        municipalitiesList().then((result) => {
            let options = [];
            result?.data?.records.map((municipality) => {
                options.push({
                    id: municipality.id,
                    name: municipality.name,
                    postalCode: municipality.postalCode,
                });
            });
            setMunicipalities(prepareSelect(options));
        });
        viewStudents(studentId).then((result) => {
            formRef.current.setValues(result?.data);
            if(result?.data?.guardian2Id){
                setDisableGuardian2(true);
            }
        });
        viewAvatar(studentId).then((result) => {
            if (result?.data?.avatar) {
                setAvatarURL(result?.data?.avatar);
            }
        });
        guardianTypesList().then((result) => {
            let options = [];
            result?.data?.records.map((guardianType) => {
                options.push({
                    id: guardianType.id,
                    name: guardianType.name,
                });
            });
            setGuardianTypes(prepareSelect(options));
        });

        studentAddressList().then((result) => {
            let options = [];
            result?.data?.records.map((school) => {
                options.push({
                    id: school.name,
                    name: school.name,
                });
            });
            setAddress(prepareSelect(options));
        });

        guardiansList([]).then((result) => {
            let options = [];
            result?.data?.records.map((guardian) => {
                options.push({
                    id: guardian.id,
                    name: guardian.firstName + ' ' + guardian.lastName + ' | ' + guardian.email + ' | ' + guardian.phoneNumber,
                    firstName: guardian.firstName,
                    lastName: guardian.lastName,
                    phoneNumber: guardian.phoneNumber,
                    email: guardian.email,
                    idCardNumber: guardian.idCardNumber,
                    type: guardian.type,
                    address: guardian.address,
                    municipality: guardian.municipality,
                });
            });
            setGuardians(prepareSelect(options));
        });
    };

    useEffect(() => {
        loadAll();
    }, [avatarURL]);

    return (
        <DefaultLayout>
            
            <TopNavigation>
                <ButtonBack to="/students" />
                <Button title={t("save")} isDisabled={isUpdateingStudent} icon="fa-regular fa-floppy-disk" onClick={onSave}></Button>
            </TopNavigation>
            <Tabs tabs={tabs} />
            <Main>
                <Breadcrumps>
                    <Link title={t("students.title")} icon="fa-user-graduate" />
                    <Link title={t("details")} />
                    <Link title={t("students.editStudent")} isActive={true} />
                </Breadcrumps>
                <Formik
                    innerRef={formRef}
                    initialValues={{
                        isActive: "",
                        gender: "",
                        username: "",
                        firstName: "",
                        lastName: "",
                        emailAddress: "",
                        emailAddressPrivate: "",
                        dateOfBirth: "",
                        isNew: false,
                        canBePhotographed: false,
                        canGoHomeAlone: false,
                        schoolOrKindergarten: "",
                        municipality: "",
                        hasCatering: false,
                        phoneNumber: "",
                        note: "",
                        guardian1FirstName: "",
                        guardian1LastName: "",
                        guardian1PhoneNumber: "",
                        guardian1Email: "",
                        guardian1IdNumber: "",
                        guardian1Type: "",
                        guardian2FirstName: "",
                        guardian2LastName: "",
                        guardian2PhoneNumber: "",
                        guardian2Email: "",
                        guardian2IdNumber: "",
                        guardian2Type: "",
                    }}
                    onSubmit={onSaved}
                >
                    {({ values, handleChange, setFieldValue }) => (
                        <Form>
                            <Row>
                                <Field isFullWidth={true}>
                                    <Avatar
                                        name="avatar"
                                        onChange={(event) => {
                                            const file = event.currentTarget.files[0];
                                            setFieldValue("avatar", file);
                                            addAvatar({ file, studentId }).then((result) => {
                                                viewAvatar(studentId).then((result) => {
                                                    if (result?.data?.avatar) {
                                                        setAvatarURL(result?.data?.avatar);
                                                    }
                                                });
                                            });
                                        }}
                                        url={avatarURL}
                                        onRemove={onAvatarRemove}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field>
                                    <Input
                                        name="firstName"
                                        label={t("students.firstName")}
                                        required={true}
                                        value={values.firstName}
                                        onChange={handleChange}
                                        error={formErrors?.firstName}
                                    />
                                </Field>
                                <Field>
                                    <Input
                                        name="lastName"
                                        label={t("students.lastName")}
                                        required={true}
                                        value={values.lastName}
                                        onChange={handleChange}
                                        error={formErrors?.lastName}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field>
                                    <Select
                                        name="gender"
                                        label={t("students.gender")}
                                        required={true}
                                        options={prepareSelect([
                                            { id: true, name: t("students.genderF") },
                                            { id: false, name: t("students.genderM") },
                                        ])}
                                        onChange={(e) => {
                                            handleChange(e);
                                        }}
                                        selectedValue={values.gender}
                                        error={formErrors?.gender}
                                        searchVisible={false}
                                    />
                                </Field>
                                <Field>
                                    <Select
                                        name="isActive"
                                        label={t("students.isActive")}
                                        required={true}
                                        options={prepareSelect([
                                            { id: true, name: t("students.canBePhotographedYes") },
                                            { id: false, name: t("students.canBePhotographedNo") },
                                        ])}
                                        onChange={(e) => {
                                            handleChange(e);
                                        }}
                                        selectedValue={values.isActive}
                                        error={formErrors?.isActive}
                                        searchVisible={false}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field>
                                    <DatePicker
                                        name="dateOfBirth"
                                        label={t("students.dateOfBirth")}
                                        required={true}
                                        value={values.dateOfBirth}
                                        onChange={handleChange}
                                        error={formErrors?.dateOfBirth}
                                    />
                                </Field>
                                <Field>
                                    <Input
                                        name="grade"
                                        label={t("students.grade")}
                                        value={values.grade}
                                        onChange={handleChange}
                                        error={formErrors?.grade}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field>
                                    <Input
                                        name="username"
                                        label={t("students.username")}
                                        required={true}
                                        value={values.username}
                                        onChange={handleChange}
                                        error={formErrors?.username}
                                        hint={"Korisničko ime će se koristiti za login polaznika"}
                                        disabled={true}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field isFullWidth={true}>
                                    <Select
                                        name="schoolOrKindergarten"
                                        label={t("students.schoolOrKindergarten")}
                                        required={true}
                                        options={schools}
                                        onChange={handleChange}
                                        selectedValue={values.schoolOrKindergarten}
                                        error={formErrors?.schoolOrKindergarten}
                                        searchInDropdown={false}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field>
                                    <Select
                                        name="isNew"
                                        label={t("students.isNew")}
                                        required={true}
                                        options={prepareSelect([
                                            { id: "true", name: t("students.isNewYes") },
                                            { id: "false", name: t("students.isNewNo") },
                                        ])}
                                        onChange={(e) => {
                                            handleChange(e);
                                            setFieldValue("isNew", e.target.value === "true");
                                        }}
                                        selectedValue={values.isNew ? "true" : "false"}
                                        error={formErrors?.isNew}
                                        searchVisible={false}
                                    />
                                </Field>
                                <Field>
                                    <Select
                                        name="canBePhotographed"
                                        label={t("students.canBePhotographed")}
                                        options={prepareSelect([
                                            { id: "true", name: t("students.canBePhotographedYes") },
                                            { id: "false", name: t("students.canBePhotographedNo") },
                                        ])}
                                        onChange={(e) => {
                                            handleChange(e);
                                            setFieldValue("canBePhotographed", e.target.value === "true");
                                        }}
                                        selectedValue={values.canBePhotographed ? "true" : "false"}
                                        error={formErrors?.canBePhotographed}
                                        searchVisible={false}
                                    />
                                </Field>
                            </Row>
                            {/* <Row>
                                <Field>
                                    <Select
                                        name="hasCatering"
                                        label={t("students.hasCatering")}
                                        options={prepareSelect([
                                            { id: "true", name: t("students.hasCateringYes") },
                                            { id: "false", name: t("students.hasCateringNo") },
                                        ])}
                                        onChange={(e) => {
                                            handleChange(e);
                                            setFieldValue("hasCatering", e.target.value === "true");
                                        }}
                                        selectedValue={values.hasCatering ? "true" : "false"}
                                        error={formErrors?.hasCatering}
                                        searchVisible={false}
                                    />
                                </Field>
                                <Field>
                                    <Select
                                        name="canGoHomeAlone"
                                        label={t("students.canGoHomeAlone")}
                                        options={prepareSelect([
                                            { id: "true", name: t("students.canGoHomeAloneYes") },
                                            { id: "false", name: t("students.canGoHomeAloneNo") },
                                        ])}
                                        onChange={(e) => {
                                            handleChange(e);
                                            setFieldValue("canGoHomeAlone", e.target.value === "true");
                                        }}
                                        selectedValue={values.canGoHomeAlone ? "true" : "false"}
                                        error={formErrors?.canGoHomeAlone}
                                        searchVisible={false}
                                    />
                                </Field>
                            </Row> */}
                            <Row>
                                <Field isFullWidth={true}>
                                    <SummerNote
                                        saving={saving}
                                        height="200px"
                                        name="note"
                                        label={t("groups.note")}
                                        value={values.note}
                                        onChange={handleChange}
                                        error={formErrors?.note}
                                    />
                                </Field>
                            </Row>
                            <Divider title={t("students.guardian1")} />
                            <Row>
                                <Field>
                                    <Input
                                        name="guardian1FirstName"
                                        label={t("employees.firstName")}
                                        required={true}
                                        value={values.guardian1FirstName}
                                        onChange={handleChange}
                                        error={formErrors?.guardian1FirstName}
                                        disabled={true}
                                    />
                                </Field>
                                <Field>
                                    <Input
                                        name="guardian1LastName"
                                        label={t("employees.lastName")}
                                        required={true}
                                        value={values.guardian1LastName}
                                        onChange={handleChange}
                                        error={formErrors?.guardian1LastName}
                                        disabled={true}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field>
                                    <Input
                                        name="emailAddress"
                                        label={t("employees.email")}
                                        required={true}
                                        value={values.guardian1Email}
                                        onChange={handleChange}
                                        error={formErrors?.emailAddress}
                                        disabled={true}
                                    />
                                </Field>
                                <Field>
                                    <Input
                                        name="phoneNumber"
                                        label={t("employees.phoneNumber")}
                                        required={true}
                                        value={values.guardian1PhoneNumber}
                                        onChange={handleChange}
                                        error={formErrors?.phoneNumber}
                                        disabled={true}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field>
                                    <Select
                                        name="municipality"
                                        label={t("students.municipality")}
                                        required={true}
                                        options={municipalities}
                                        onChange={handleChange}
                                        selectedValue={values.municipality}
                                        error={formErrors?.municipality}
                                        disabled={true}
                                    />
                                </Field>
                                <Field>
                                    <Select
                                        name="address"
                                        label={t("students.address")}
                                        required={true}
                                        options={address}
                                        onChange={handleChange}
                                        selectedValue={values.address}
                                        error={formErrors?.address}
                                        allowAppendText={true}
                                        disabled={true}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field>
                                    <Input
                                        name="guardian1IdNumber"
                                        label={t("students.idNumber")}
                                        value={values.guardian1IdNumber}
                                        onChange={handleChange}
                                        error={formErrors?.guardian1IdNumber}
                                        disabled={true}
                                    />
                                </Field>
                                <Field>
                                    <Select
                                        name="guardian1Type"
                                        label={t("students.guardianType")}
                                        required={true}
                                        options={guardianTypes}
                                        onChange={handleChange}
                                        selectedValue={values.guardian1Type}
                                        error={formErrors?.guardian1Type}
                                        searchVisible={false}
                                        disabled={true}
                                    />
                                </Field>
                            </Row>
                            <Divider title={t("students.guardian2")} />
                            {!disableGuardian2 && (
                                <Row>
                                    <Field>
                                        <Select
                                            name="guardian2Id"
                                            label={t("guardians.title")}
                                            required={true}
                                            options={guardians}
                                            onChange={(e) => {
                                                handleChange(e);
                                                const selectedGuardian = guardians.find(g => g.id === e.target.value);
                                                if (selectedGuardian) {
                                                setFieldValue("guardian2FirstName", selectedGuardian.firstName || "");
                                                setFieldValue("guardian2LastName", selectedGuardian.lastName || "");
                                                setFieldValue("guardian2PhoneNumber", selectedGuardian.phoneNumber || "");
                                                setFieldValue("guardian2Email", selectedGuardian.email || "");
                                                setFieldValue("guardian2IdNumber", selectedGuardian.idCardNumber || "");
                                                setFieldValue("guardian2Type", selectedGuardian.type || null);
                                                }

                                                setDisableGuardian2(true);
                                            }}
                                            selectedValue={values.guardian2Id}
                                            error={formErrors?.guardian2Id}
                                            searchVisible={disableGuardian2}
                                            searchInDropdown={disableGuardian2}
                                            disabled={disableGuardian2}
                                        />
                                    </Field>
                                </Row>
                            )}
                            <Row>
                                <Field>
                                    <Input
                                        name="guardian2FirstName"
                                        label={t("employees.firstName")}
                                        value={values.guardian2FirstName}
                                        onChange={handleChange}
                                        error={formErrors?.guardian2FirstName}
                                        disabled={disableGuardian2}
                                    />
                                </Field>
                                <Field>
                                    <Input
                                        name="guardian2LastName"
                                        label={t("employees.lastName")}
                                        value={values.guardian2LastName}
                                        onChange={handleChange}
                                        error={formErrors?.guardian2LastName}
                                        disabled={disableGuardian2}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field>
                                    <Input
                                        name="guardian2Email"
                                        label={t("employees.email")}
                                        value={values.guardian2Email}
                                        onChange={handleChange}
                                        error={formErrors?.guardian2Email}
                                        disabled={disableGuardian2}
                                    />
                                </Field>
                                <Field>
                                    <Input
                                        name="guardian2PhoneNumber"
                                        label={t("employees.phoneNumber")}
                                        value={values.guardian2PhoneNumber}
                                        onChange={handleChange}
                                        error={formErrors?.guardian2PhoneNumber}
                                        disabled={disableGuardian2}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field>
                                    <Input
                                        name="guardian2IdNumber"
                                        label={t("students.idNumber")}
                                        value={values.guardian2IdNumber}
                                        onChange={handleChange}
                                        error={formErrors?.guardian2IdNumber}
                                        disabled={disableGuardian2}
                                    />
                                </Field>
                                <Field>
                                    <Select
                                        name="guardian2Type"
                                        label={t("students.guardianType")}
                                        options={guardianTypes}
                                        onChange={handleChange}
                                        selectedValue={values.guardian2Type}
                                        error={formErrors?.guardian2Type}
                                        searchVisible={false}
                                        disabled={disableGuardian2}
                                    />
                                </Field>
                            </Row>
                        </Form>
                    )}
                </Formik>
            </Main>
        </DefaultLayout>
    );
};

export default AddStudent;
